import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';

const ProductAccuracyChart = ({ productAccuracy, handleDrillDown }) => {
  const chartRef = useRef(null);
  
  if (!productAccuracy || productAccuracy.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-12">
        <div className="text-6xl mb-4 text-gray-300">📦</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Product Accuracy</h3>
        <p className="text-gray-600">No data available</p>
        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or date range</p>
      </div>
    );
  }

  const sortedData = [...productAccuracy].sort((a, b) => a.accuracy - b.accuracy);
  
  console.log("sortedData", sortedData);
  
  // Reduced bar width since we're truncating product names
  const minBarWidth = 28; // Slightly wider for better readability
  const chartWidth = Math.max(900, sortedData.length * minBarWidth);
  
  // Helper function to truncate text
  const truncateText = (text, maxLength = 18) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  const chartData = {
    labels: sortedData.map(item => truncateText(item.product)),
    datasets: [
      {
        label: 'Accuracy %',
        data: sortedData.map(item => item.accuracy),
        backgroundColor: '#4f46e5',
        borderColor: '#4f46e5', 
        borderWidth: 0,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (event, activeElements) => {
      if (event?.native) {
        event.native.target.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default';
      }
    },
    interaction: {
      intersect: false
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: '#f3f4f6',
          borderColor: '#e5e7eb'
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
            family: 'system-ui'
          }
        },
        title: {
          display: true,
          text: 'Accuracy (%)',
          color: '#374151',
          font: {
            size: 13,
            weight: '600',
            family: 'system-ui'
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
          color: '#6b7280',
          font: {
            size: 11,
            family: 'system-ui'
          }
        }
      }
    },
    plugins: {
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#ffffff',
        bodyColor: '#e5e7eb',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context) {
            // Show full product name in tooltip title
            const dataIndex = context[0].dataIndex;
            return sortedData[dataIndex].product;
          },
          label: function(context) {
            return [
              `Accuracy: ${context.parsed.y.toFixed(2)}%`,
              'Click to drill down'
            ];
          }
        }
      },
      legend: {
        display: false
      }
    }
  };

  return (
    <div className="bg-white p-6">
      <div className="mb-6">
        <div className="flex items-center justify-end">
          <span className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full border border-gray-200">
            {sortedData.length} Products
          </span>
        </div>
      </div>
      
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="overflow-x-auto" style={{ position: 'relative' }}>
          <div style={{ minWidth: '100%', width: `${chartWidth}px`, height: '400px' }}>
            <Bar 
              ref={chartRef}
              data={chartData} 
              options={options} 
              onClick={(event) => {
                const chart = chartRef.current;
                if (chart) {
                  const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
                  if (elements.length > 0) {
                    const dataIndex = elements[0].index;
                    const productData = sortedData[dataIndex];
                    handleDrillDown('product', productData);
                  }
                }
              }}
            />
          </div>
        </div>
        
        {sortedData.length > 30 && (
          <div className="text-center mt-4 text-sm text-gray-500 bg-white px-3 py-2 rounded border border-gray-200">
            <span>Scroll horizontally to view all {sortedData.length} products →</span>
          </div>
        )}
      </div>
      
      {/* Quick stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {sortedData.filter(p => p.accuracy >= 95).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">Excellent (&ge;95%)</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {sortedData.filter(p => p.accuracy >= 85 && p.accuracy < 95).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">Good (85-94%)</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {sortedData.filter(p => p.accuracy >= 70 && p.accuracy < 85).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">Average (70-84%)</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {sortedData.filter(p => p.accuracy < 70).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">Needs Attention (&lt;70%)</div>
        </div>
      </div>
    </div>
  );
};

export default ProductAccuracyChart;